import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Suspense,
} from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  MdRefresh,
  MdSearch,
  MdClose,
  MdInsertDriveFile,
  MdPictureAsPdf,
  MdScience,
  MdErrorOutline,
  MdFilterAlt,
} from "react-icons/md";
import { useAuth } from "../../Context/AuthContext";
import { useChartTheme } from "../../Context/ThemeContext";
import {
  PermissionCodes,
  permissionHas,
  resolveUserPermissionCodes,
} from "../../Utils/Permissions";
import PageTitle from "../../Components/Common/PageTitle";
import SearchableDropdown from "../../Components/Subscription/SearchableDropdown";
import CustomDatePicker from "../../Components/Common/CustomDatePicker";
import DataGrid from "../../Components/Table/DataGrid";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import {
  getValueBasedCareAnalytics,
  getValueBasedCareFilters,
} from "../../Services/ValueBasedCareService";
import {
  normalizeCohort,
  searchRows,
  toOptions,
  formatDate,
  parseDateTimestamp,
  AGE_BANDS,
  buildAgeVsDxFromCohort,
  ageVsDxOption,
  buildAgeVsMedicationFromCohort,
  ageVsMedicationOption,
  exportCohortToCsv,
  exportCohortToPdf,
} from "../../Utils/valueBasedCareUtils";
import BackButton from "../../Components/Common/BackButton";

const BaseChart = React.lazy(() => import("../../Components/Analytics/BaseChart"));

const HERO_GRADIENT = "var(--hc-vbc-hero-gradient)";
const DEFAULT_START = "2026-01-01";
const DEFAULT_END = "2026-07-31";
const DATE_MIN = "2020-01-01";
const DATE_MAX = "2030-12-31";

const FILTER_COL = "col-12 col-sm-6 col-lg-4";
const DROPDOWN_PAGE_SIZE = 50;

function FilterLabel({ children }) {
  return (
    <label className="form-label fw-semibold text-secondary text-uppercase mb-1" style={{ fontSize: "0.7rem" }}>
      {children}
    </label>
  );
}

function FilterField({ label, children }) {
  return (
    <div className={FILTER_COL}>
      <FilterLabel>{label}</FilterLabel>
      {children}
    </div>
  );
}

export default function ValueBasedCareView() {
  const chartTheme = useChartTheme();
  const params = useParams();
  const locationState = useLocation();
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();

  const organizationId = params?.id || locationState?.state?.organization?.id || "";
  const organizationName =
    locationState?.state?.organization?.organization_name ||
    locationState?.state?.organization?.OrganizationName ||
    "Organization";

  const userPermissionCodes = useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken]
  );
  const canView = permissionHas(userPermissionCodes, PermissionCodes.VALUE_BASED_CARE_VIEW, decodedToken);
  const generatedBy =
    decodedToken?.username || decodedToken?.user_email || decodedToken?.email || "";

  const [insurance, setInsurance] = useState("");
  const [provider, setProvider] = useState("");
  const [location, setLocation] = useState("");
  const [dxCode, setDxCode] = useState("");
  const [medications, setMedications] = useState([]);
  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);
  const [search, setSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState({
    insurances: [],
    providers: [],
    locations: [],
    dxCodes: [],
    medications: [],
  });

  const [cohort, setCohort] = useState([]);
  const [summary, setSummary] = useState(null);
  const [ageVsDx, setAgeVsDx] = useState(null);
  const [ageVsMedication, setAgeVsMedication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getValueBasedCareFilters(token, organizationId);
        if (cancelled || !res?.is_success || !res.data) return;
        setFilterOptions({
          insurances: res.data.insurances || [],
          providers: res.data.providers || [],
          locations: res.data.locations || [],
          dxCodes: res.data.dxCodes || [],
          medications: res.data.medications || [],
        });
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, organizationId]);

  const load = useCallback(() => {
    if (!token) return;
    const rid = ++reqId.current;
    setLoading(true);
    setError("");
    getValueBasedCareAnalytics(token, {
      organizationId,
      insurance,
      provider,
      location,
      startDate,
      endDate,
      dxCode,
      medications,
    })
      .then((res) => {
        if (reqId.current !== rid) return;
        if (res?.is_success && res.data) {
          setCohort(normalizeCohort(res.data.patients || []));
          setSummary(res.data.summary || null);
          setAgeVsDx(res.data.ageVsDx || null);
          setAgeVsMedication(res.data.ageVsMedication || null);
        } else {
          setCohort([]);
          setSummary(null);
          setAgeVsDx(null);
          setAgeVsMedication(null);
          setError(res?.message || "This analytics view is not available.");
        }
      })
      .catch((err) => {
        if (reqId.current !== rid) return;
        setCohort([]);
        setSummary(null);
        setAgeVsDx(null);
        setAgeVsMedication(null);
        setError(err?.message || "Network error while loading Value-Based Care analytics.");
      })
      .finally(() => {
        if (reqId.current === rid) setLoading(false);
      });
  }, [token, organizationId, insurance, provider, location, startDate, endDate, dxCode, medications]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const tableRows = useMemo(() => {
    const rows = searchRows(cohort, search);
    return [...rows].sort(
      (a, b) => parseDateTimestamp(a.dateOfService) - parseDateTimestamp(b.dateOfService)
    );
  }, [cohort, search]);
  const dxOption = useMemo(() => {
    const data =
      ageVsDx && Array.isArray(ageVsDx.bands) && ageVsDx.bands.length === AGE_BANDS.length
        ? ageVsDx
        : buildAgeVsDxFromCohort(cohort);
    return ageVsDxOption(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageVsDx, cohort, chartTheme]);
  const medOption = useMemo(() => {
    const data =
      ageVsMedication &&
        Array.isArray(ageVsMedication.bands) &&
        ageVsMedication.bands.length === AGE_BANDS.length
        ? ageVsMedication
        : buildAgeVsMedicationFromCohort(cohort);
    return ageVsMedicationOption(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ageVsMedication, cohort, chartTheme]);

  const activeFilterCount =
    [insurance, provider, location, dxCode].filter(Boolean).length +
    (medications.length ? 1 : 0) +
    (startDate !== DEFAULT_START || endDate !== DEFAULT_END ? 1 : 0);

  const clearFilters = useCallback(() => {
    setInsurance("");
    setProvider("");
    setLocation("");
    setDxCode("");
    setMedications([]);
    setStartDate(DEFAULT_START);
    setEndDate(DEFAULT_END);
  }, []);

  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const providerLabel = provider || "All providers";
  const locationLabel = location || "All locations";

  const exportMeta = useCallback(
    () => ({
      insuranceLabel: `${organizationName}${insurance ? ` · ${insurance}` : ""}`,
      providerLabel,
      locationLabel,
      dxCode,
      medication: medications.join("; "),
      startDate,
      endDate,
      patientCount: summary?.patients ?? cohort.length,
      generatedAt: new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }),
      generatedBy,
    }),
    [organizationName, insurance, providerLabel, locationLabel, dxCode, medications, startDate, endDate, summary, cohort.length, generatedBy]
  );
  const handleCsv = useCallback(async () => {
    if (exportingCsv || exportingPdf) return;
    try {
      setExportingCsv(true);
      await new Promise((resolve) => setTimeout(resolve, 60));
      exportCohortToCsv({ rows: tableRows, meta: exportMeta() });
    } finally {
      setExportingCsv(false);
    }
  }, [tableRows, exportMeta, exportingCsv, exportingPdf]);

  const handlePdf = useCallback(async () => {
    if (exportingCsv || exportingPdf) return;
    try {
      setExportingPdf(true);
      await new Promise((resolve) => setTimeout(resolve, 60));
      exportCohortToPdf({ rows: tableRows, meta: exportMeta() });
    } finally {
      setExportingPdf(false);
    }
  }, [tableRows, exportMeta, exportingCsv, exportingPdf]);

  const columns = useMemo(
    () => [
      {
        id: "patientName",
        label: "Patient Name",
        minWidth: 190,
        filterable: true,
        sortAccessor: (row) => row.patientName || "",
        format: (_v, row) => <span className="fw-semibold text-dark">{row.patientName}</span>,
      },
      {
        id: "age",
        label: "Age",
        minWidth: 64,
        align: "center",
        filterable: true,
        sortAccessor: (row) => (Number.isFinite(row.age) ? row.age : -1),
        format: (_v, row) => (Number.isFinite(row.age) ? row.age : "—"),
      },
      {
        id: "dxCode",
        label: "Dx — ICD-10",
        minWidth: 120,
        filterable: true,
        sortAccessor: (row) => row.dxCode || "",
        format: (_v, row) =>
          row.dxCode ? (
            <span
              className="fw-bold rounded-1 px-2 py-1 d-inline-block"
              style={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: "0.78rem",
                backgroundColor: "rgba(var(--hc-gradient-mid-rgb), 0.12)",
                color: "var(--hc-teal-text-alt, #0f766e)",
              }}
            >
              {row.dxCode}
            </span>
          ) : (
            <span className="text-secondary">—</span>
          ),
      },
      {
        id: "medication",
        label: "Medication",
        minWidth: 200,
        filterable: true,
        sortAccessor: (row) =>
          Array.isArray(row.medication)
            ? row.medication.length
            : row.medication
              ? 1
              : 0,
        filterAccessor: (row) =>
          Array.isArray(row.medication)
            ? row.medication.join(" ; ")
            : String(row.medication || ""),
        optionsAccessor: (row) => {
          const v = row.medication;
          if (Array.isArray(v)) return v.filter(Boolean);
          if (typeof v === "string" && v.trim() && v !== "—") {
            return v.split(/\r?\n|;/).map((s) => s.trim()).filter(Boolean);
          }
          return [];
        },
        format: (_v, row) => {
          const list = Array.isArray(row.medication)
            ? row.medication.filter(Boolean)
            : typeof row.medication === "string" &&
              row.medication.trim() &&
              row.medication !== "—"
              ? row.medication.split(/\r?\n|;/).map((s) => s.trim()).filter(Boolean)
              : [];

          if (!list.length) {
            return (
              <span
                className="fst-italic"
                style={{ color: "var(--hc-status-inactive-text, #6b7280)" }}
              >
                —
              </span>
            );
          }

          const primaryMed = list[0];
          const hasMore = list.length > 1;

          return (
            <Tooltip
              title={
                <div style={{ padding: "4px 2px", fontSize: "0.82rem", lineHeight: 1.45, maxWidth: 380 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      marginBottom: 6,
                      paddingBottom: 4,
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    Prescribed Medications ({list.length})
                  </div>
                  {list.map((m, i) => (
                    <div key={i} style={{ marginBottom: i < list.length - 1 ? 6 : 0 }}>
                      <span style={{ color: "var(--hc-vbc-tooltip-dot, #38bdf8)", marginRight: 4 }}>•</span> {m}
                    </div>
                  ))}
                </div>
              }
              arrow
              placement="top-start"
            >
              <div
                className="d-inline-flex align-items-center gap-1 text-dark"
                style={{
                  maxWidth: 240,
                  cursor: "pointer",
                }}
              >
                <span
                  className="text-truncate d-inline-block"
                  style={{
                    maxWidth: hasMore ? 180 : 230,
                    fontSize: "0.82rem",
                    verticalAlign: "middle",
                  }}
                  title={primaryMed}
                >
                  {primaryMed}
                </span>
                {hasMore && (
                  <span
                    className="badge rounded-pill bg-light text-primary border px-1"
                    style={{ fontSize: "0.72rem", fontWeight: 700, flexShrink: 0 }}
                  >
                    +{list.length - 1}
                  </span>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: "nextAppointment",
        label: "Next Appointment",
        minWidth: 150,
        filterable: true,
        sortAccessor: (row) => parseDateTimestamp(row.nextAppointment),
        filterAccessor: (row) => (row.nextAppointment ? formatDate(row.nextAppointment) : ""),
        format: (_v, row) =>
          row.nextAppointment ? (
            formatDate(row.nextAppointment)
          ) : (
            <span
              className="fst-italic"
              style={{ color: "var(--hc-status-inactive-text, #6b7280)" }}
              title="No upcoming appointment on file"
            >
              —
            </span>
          ),
      },
      {
        id: "provider",
        label: "Provider",
        minWidth: 170,
        filterable: true,
        sortAccessor: (row) => row.provider || "",
        format: (_v, row) => row.provider || <span className="text-secondary">—</span>,
      },
      {
        id: "location",
        label: "Location",
        minWidth: 160,
        filterable: true,
        sortAccessor: (row) => row.location || "",
        format: (_v, row) => row.location || <span className="text-secondary">—</span>,
      },
      {
        id: "dateOfService",
        label: "Date of Service",
        minWidth: 140,
        filterable: true,
        sortAccessor: (row) => parseDateTimestamp(row.dateOfService),
        filterAccessor: (row) => formatDate(row.dateOfService),
        format: (_v, row) => formatDate(row.dateOfService),
      },
    ],
    []
  );

  if (!canView) {
    return (
      <div className="container-fluid py-4">
        <PageTitle title="Value-Based Care | Aegis Health 360" />
        <div className="alert alert-danger">You do not have permission to view Value-Based Care analytics.</div>
      </div>
    );
  }

  const heroTiles = [
    { k: "Patients", v: summary?.patients },
    { k: "Records", v: summary?.encounters },
    { k: "Diagnoses (ICD-10)", v: summary?.distinctDx },
    { k: "Median age", v: summary?.medianAge, plain: true },
    {
      k: "Age range",
      v: summary?.ageMin != null && summary?.ageMax != null ? `${summary.ageMin}–${summary.ageMax}` : "—",
      plain: true,
    },
  ];

  return (
    <div className="container-fluid py-4">
      <PageTitle title={`Value-Based Care · ${organizationName} | Aegis Health 360`} />

      <BackButton onClick={() => navigate(-1)} className="mb-3" />

      <div className="rounded-4 shadow text-white p-4 p-lg-5 overflow-hidden" style={{ background: HERO_GRADIENT }}>
        <div className="row align-items-stretch g-3 g-md-4">
          <div className="col-12 col-lg-7 col-xl-6 d-flex flex-column justify-content-between gap-3">
            <div>
              <span
                className="d-inline-flex align-items-center gap-2 rounded-pill fw-semibold text-uppercase bg-white bg-opacity-10 px-3 py-1 mb-2"
                style={{ fontSize: "0.72rem", letterSpacing: "0.12em" }}
              >
                <MdScience size={14} />
                Value-Based Care · Incentive Program
              </span>
              <h1 className="fw-bold m-0 text-uppercase" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2.2rem)", letterSpacing: "0.02em", lineHeight: 1.2 }}>
                {organizationName}
              </h1>
            </div>

            <div className="row g-2 g-sm-3 w-100 my-0">
              <div className="col-6">
                <div
                  className="bg-white bg-opacity-10 border border-white border-opacity-25 rounded-3 p-2 px-3 text-center d-flex flex-column justify-content-center shadow-sm h-100"
                  style={{ minHeight: 64 }}
                >
                  <div className="fw-bold mb-0 lh-1" style={{ fontSize: "1.12rem" }}>
                    {loading ? (
                      <span className="d-inline-block skeleton-hero rounded-2" style={{ width: 84, height: "1.15rem" }} />
                    ) : (
                      formatDate(startDate)
                    )}
                  </div>
                  <div className="text-white text-opacity-75 mt-1 text-uppercase" style={{ fontSize: "0.68rem", fontWeight: 500, letterSpacing: "0.04em" }}>
                    DOS Start Date
                  </div>
                </div>
              </div>
              <div className="col-6">
                <div
                  className="bg-white bg-opacity-10 border border-white border-opacity-25 rounded-3 p-2 px-3 text-center d-flex flex-column justify-content-center shadow-sm h-100"
                  style={{ minHeight: 64 }}
                >
                  <div className="fw-bold mb-0 lh-1" style={{ fontSize: "1.12rem" }}>
                    {loading ? (
                      <span className="d-inline-block skeleton-hero rounded-2" style={{ width: 84, height: "1.15rem" }} />
                    ) : (
                      formatDate(endDate)
                    )}
                  </div>
                  <div className="text-white text-opacity-75 mt-1 text-uppercase" style={{ fontSize: "0.68rem", fontWeight: 500, letterSpacing: "0.04em" }}>
                    DOS End Date
                  </div>
                </div>
              </div>
            </div>

            <div
              className="d-flex align-items-start gap-2 rounded-3 bg-white bg-opacity-10 border border-white border-opacity-25 px-3 py-2 w-100"
              style={{ fontSize: "0.78rem", borderStyle: "dashed" }}
            >
              <MdScience size={14} className="flex-shrink-0 mt-1" />
              <span className="opacity-90">
                Patient, Age and Dx (ICD-10) are live from the record. Medication and Next Appointment
                come from the third-party feed — Medication is the prescriptions dispensed within 6 days
                of the date of service; Next Appointment is the patient&apos;s next upcoming booking.
              </span>
            </div>
          </div>

          <div className="col-12 col-lg-5 col-xl-6">
            <div
              className="d-grid gap-2 gap-sm-3 h-100"
              style={{
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gridTemplateRows: "repeat(3, minmax(0, 1fr))",
              }}
            >
              {heroTiles.map((t, idx) => (
                <div
                  key={t.k}
                  className="bg-white bg-opacity-10 border border-white border-opacity-25 rounded-3 p-2 px-3 text-center d-flex flex-column justify-content-center h-100 shadow-sm"
                  style={{
                    gridColumn: idx === 4 ? "span 2" : "span 1",
                    minHeight: 64,
                  }}
                >
                  <div className="fw-bold mb-0 lh-1" style={{ fontSize: "1.35rem" }}>
                    {loading ? (
                      <span className="d-inline-block skeleton-hero rounded-2" style={{ width: idx === 4 ? 64 : 52, height: "1.35rem" }} />
                    ) : t.v === null || t.v === undefined ? (
                      "—"
                    ) : (
                      t.v
                    )}
                  </div>
                  <div className="text-white text-opacity-75 mt-1" style={{ fontSize: "0.72rem", fontWeight: 500 }}>
                    {t.k}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="bg-white rounded-4 shadow-sm p-3 p-md-4 position-relative">
          <button
            type="button"
            className="btn btn-sm border position-absolute d-inline-flex align-items-center justify-content-center"
            style={{ top: 12, right: 12, width: 34, height: 34, borderRadius: 10 }}
            onClick={load}
            disabled={loading}
            title="Refresh"
            aria-label="Refresh"
          >
            {loading ? (
              <span className="spinner-border spinner-border-sm" style={{ width: 15, height: 15 }} />
            ) : (
              <MdRefresh size={16} />
            )}
          </button>

          <div className="d-flex flex-wrap align-items-center gap-3 mb-3" style={{ marginRight: 44, minHeight: 22 }}>
            <div className="d-flex align-items-center gap-2 text-secondary text-uppercase fw-bold" style={{ fontSize: "0.7rem", letterSpacing: "0.1em" }}>
              <MdFilterAlt size={15} style={{ color: "var(--hc-accent-teal)" }} />
              Filters
            </div>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none d-inline-flex align-items-center gap-1 p-0 fw-semibold"
                style={{ fontSize: "0.75rem", color: "var(--hc-teal-text-alt, #0f766e)" }}
                onClick={clearFilters}
                title="Reset all filters"
              >
                <MdClose size={14} />
                Clear
                <span
                  className="badge rounded-pill"
                  style={{
                    fontSize: "0.66rem",
                    backgroundColor: "rgba(var(--hc-gradient-mid-rgb), 0.14)",
                    color: "var(--hc-teal-text-alt, #0f766e)",
                  }}
                >
                  {activeFilterCount}
                </span>
              </button>
            ) : null}
          </div>
          <div className="row g-3 align-items-end mb-4">
            <FilterField label="Insurance">
              <SearchableDropdown
                options={toOptions(filterOptions.insurances, "All insurances")}
                value={insurance}
                onChange={(v) => setInsurance(v || "")}
                placeholder="All insurances"
                emptyMessage="No insurances found"
              />
            </FilterField>
            <FilterField label="Provider">
              <SearchableDropdown
                options={toOptions(filterOptions.providers, "All providers")}
                value={provider}
                onChange={(v) => setProvider(v || "")}
                placeholder="All providers"
                emptyMessage="No providers found"
              />
            </FilterField>
            <FilterField label="Location">
              <SearchableDropdown
                options={toOptions(filterOptions.locations, "All locations")}
                value={location}
                onChange={(v) => setLocation(v || "")}
                placeholder="All locations"
                emptyMessage="No locations found"
              />
            </FilterField>
            <FilterField label="Dx (ICD-10)">
              <SearchableDropdown
                options={toOptions(filterOptions.dxCodes, "All diagnoses")}
                value={dxCode}
                onChange={(v) => setDxCode(v || "")}
                placeholder="All diagnoses"
                emptyMessage="No diagnoses found"
                pageSize={DROPDOWN_PAGE_SIZE}
              />
            </FilterField>
            <FilterField label="Medication">
              <SearchableDropdown
                options={toOptions(filterOptions.medications, "All medications")}
                multiple
                value={medications}
                onChange={(v) => setMedications(Array.isArray(v) ? v : [])}
                placeholder="All medications"
                emptyMessage="No medications found"
                pageSize={DROPDOWN_PAGE_SIZE}
              />
            </FilterField>
            <div className={FILTER_COL}>
              <div className="row g-2">
                <div className="col-6">
                  <FilterLabel>DOS — from</FilterLabel>
                  <CustomDatePicker
                    id="vbc-start-date"
                    value={startDate}
                    onChange={(val) => setStartDate(val || DEFAULT_START)}
                    maxDate={endDate ? dayjs(endDate) : undefined}
                    minDate={dayjs(DATE_MIN)}
                  />
                </div>
                <div className="col-6">
                  <FilterLabel>DOS — to</FilterLabel>
                  <CustomDatePicker
                    id="vbc-end-date"
                    value={endDate}
                    onChange={(val) => setEndDate(val || DEFAULT_END)}
                    minDate={startDate ? dayjs(startDate) : dayjs(DATE_MIN)}
                    maxDate={dayjs(DATE_MAX)}
                  />
                </div>
              </div>
            </div>
          </div>

          {error ? (
            <div className="border border-dashed rounded-4 text-center py-5 px-3" style={{ borderStyle: "dashed" }}>
              <MdErrorOutline size={28} className="text-danger mb-2" />
              <p className="text-secondary mb-3">{error}</p>
              <button type="button" className="btn btn-sm text-white" style={{ background: "var(--hc-accent-fill)" }} onClick={load}>
                <MdRefresh size={15} className="me-1" /> Try again
              </button>
            </div>
          ) : (
            <>
              <div className="d-flex flex-column gap-4 mb-4">
                <div>
                  <div className="mb-2">
                    <h3 className="fw-bold mb-0" style={{ fontSize: "1rem" }}>Age vs Dx (ICD-10)</h3>
                    <p className="text-secondary mb-0" style={{ fontSize: "0.8rem" }}>
                      Every diagnosis by patient count, segmented by age band — scroll for more
                    </p>
                  </div>
                  <Suspense fallback={<div style={{ height: 440 }} />}>
                    <BaseChart
                      title=""
                      option={dxOption || {}}
                      height="440px"
                      loading={loading}
                      noData={!loading && !dxOption}
                      info={{
                        title: "Age vs Dx (ICD-10)",
                        sections: [
                          { h: "What this shows", b: "Every ICD-10 diagnosis in the patients currently listed below, as a bar split by age group. Use the scrollbar on the right (or the mouse wheel over the chart) to move through the full list; the axis stays fixed." },
                          { h: "How to read it", b: "A longer bar means more patients have that diagnosis. The colour split shows the age mix." },
                          { h: "Where the numbers come from", b: "Each patient is counted once per diagnosis (visits.icd_code_10), using their age on the date of service." },
                        ],
                      }}
                    />
                  </Suspense>
                </div>
                <div>
                  <div className="mb-2">
                    <h3 className="fw-bold mb-0" style={{ fontSize: "1rem" }}>Age vs Medication</h3>
                    <p className="text-secondary mb-0" style={{ fontSize: "0.8rem" }}>
                      Prescriptions by age group heatmap — each column is shaded with its matching age-band color; scroll for more
                    </p>
                  </div>
                  <Suspense fallback={<div style={{ height: 440 }} />}>
                    <BaseChart
                      title=""
                      option={medOption || {}}
                      height="440px"
                      loading={loading}
                      noData={!loading && !medOption}
                      info={{
                        title: "Age vs Medication (Heatmap)",
                        sections: [
                          { h: "What this shows", b: "A heat map matrix showing prescribed medications (rows) across the 11 age brackets (columns). Each column is shaded with its corresponding age-band color from the Age vs Dx chart above, with deeper saturation indicating higher patient counts." },
                          { h: "How to read it", b: "Hover over any cell to see exact patient counts and age group details. Scroll vertically with the mouse wheel or the slider bar on the right to navigate all medications. The top age axis remains fixed." },
                          { h: "Where the numbers come from", b: "Prescriptions from the third-party Prescription by Provider export, attached to a visit when dispensed within 6 days of its date of service (visits.medication), using the patient's age on that date." },
                        ],
                      }}
                    />
                  </Suspense>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div className="position-relative flex-grow-1" style={{ minWidth: 240 }}>
                  <span
                    className="position-absolute top-50 translate-middle-y text-secondary pe-none"
                    style={{ left: 12 }}
                  >
                    <MdSearch size={18} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name, diagnosis, provider or location…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingLeft: 38, paddingRight: search ? 38 : 12, height: 42 }}
                  />
                  {search ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => setSearch("")}
                      className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                      style={{ right: 8, lineHeight: 0 }}
                    >
                      <MdClose size={16} />
                    </button>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="btn btn-sm border d-inline-flex align-items-center gap-2 fw-semibold px-3"
                  style={{ height: 42, borderRadius: 10 }}
                  onClick={handleCsv}
                  disabled={loading || exportingCsv || exportingPdf || !tableRows.length}
                  title="Download CSV / Excel spreadsheet"
                >
                  {exportingCsv ? (
                    <span className="spinner-border spinner-border-sm text-secondary" role="status" aria-hidden="true" style={{ width: 15, height: 15 }} />
                  ) : (
                    <MdInsertDriveFile size={16} />
                  )}
                  <span>CSV</span>
                </button>
                <button
                  type="button"
                  className="btn btn-sm d-inline-flex align-items-center gap-2 fw-semibold text-white px-3"
                  style={{ height: 42, borderRadius: 10, background: "var(--hc-accent-fill)" }}
                  onClick={handlePdf}
                  disabled={loading || exportingCsv || exportingPdf || !tableRows.length}
                  title="Download PDF document"
                >
                  {exportingPdf ? (
                    <span className="spinner-border spinner-border-sm text-light" role="status" aria-hidden="true" style={{ width: 15, height: 15 }} />
                  ) : (
                    <MdPictureAsPdf size={16} />
                  )}
                  <span>PDF</span>
                </button>
              </div>

              <DataGrid
                columns={columns}
                rows={tableRows}
                loading={loading}
                skeletonRows={10}
                defaultRowsPerPage={10}
                stickyHeader
                getRowId={(row) => row.id}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
